import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { DetailPageError } from '~/modules/errors/components/DetailPageError';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import { useUser } from '~settings/api/queries/user';
import { SETTINGS_ROUTES } from '~settings/config/routes';

import { UserDetailProvider } from './contexts/UserDetailProvider';
import { UserDetailsBlock } from './parts/UserDetailsBlock';
import { UserHeader } from './parts/UserHeader';
import { UserResources } from './parts/UserResources';

export function DetailUserPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('settings');
  const [isInternalUserUpdatesEnabled] = useFeatureFlags([
    FEATURE_FLAGS.SettingsInternalUserUpdates,
  ]);
  const { userId } = useParams<{ userId: string }>();
  const { isLoading, error, user } = useUser({
    userId: userId || '',
    ...(!isInternalUserUpdatesEnabled && { withRolesMap: true }),
  });

  useEffect(() => {
    if (!userId) {
      navigate(SETTINGS_ROUTES.ROOT);
    }
  }, [userId, navigate]);

  if (isLoading) {
    return <section className="py-md">{t('loading')}</section>;
  }

  if (error || !user) {
    return (
      <DetailPageError
        errorCode={error?.response?.status}
        fallbackRoute={{
          href: SETTINGS_ROUTES.ROOT,
          label: t('backToUserManagement'),
        }}
      />
    );
  }

  return (
    <UserDetailProvider user={user}>
      <article className="grid grid-cols-10 gap-md @container">
        <div className="col-span-full">
          <UserHeader />
        </div>
        <div className="col-start-1 col-end-11 flex flex-col gap-md text-body-sm @lg:col-end-5 @xl:col-end-4 @2xl:col-end-3">
          <UserDetailsBlock />
        </div>
        <div className="col-start-1 col-end-11 @lg:col-start-5 @xl:col-start-4 @2xl:col-start-3">
          <UserResources />
        </div>
      </article>
    </UserDetailProvider>
  );
}

DetailUserPage.displayName = 'DetailUserPage';
